import type { ChangeEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import {
  DECIMAL_VALIDATION_TWO_PLACES_REGEX,
  HOURS_VALIDATION_REGEX,
  MAX_10_DIGITS_ONE_DECIMAL_REGEX,
  MAX_11_DIGITS_TWO_DECIMALS_REGEX,
  PERCENTAGE_VALIDATION_REGEX,
  PHONE_VALIDATION_REGEX
} from 'utils/index';
import DropDown from './inputs/Dropdown';
import GooglePlacesAutocompleteComp from './inputs/GooglePlacesAutocomplete';
import TextField from './NewLayout/Textfield';

interface IEditableTableCellProps {
  value: string;
  onEditComplete: (val: string) => void;
  type?: 'text' | 'number' | 'date' | 'time' | 'file';
  min?: string;
  max?: string;
  isDropdown?: boolean;
  options?: { value: string; id: string | number }[];
  id?: string | number;
  apiInProgress?: boolean;
  setApiInProgress: (val: boolean) => void;
  displayValue?: string;
  isAddress?: boolean;
  selectText?: boolean;
  name: string;
  maxLength?: number;
}

function EditableTableCell({
  value,
  onEditComplete,
  type,
  min,
  max,
  isDropdown,
  options,
  id,
  apiInProgress,
  setApiInProgress,
  displayValue,
  selectText,
  isAddress,
  name,
  maxLength
}: IEditableTableCellProps) {
  const [val, setVal] = useState<string | number>('');
  const [isDropdownOptionListVisible, setDropdownOptionListVisibility] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLSelectElement>(null);
  const latestVal = useRef<string | number>(val);
  const previousVal = useRef<string | number>(value);
  const touchStartRef = useRef<boolean>(false);
  const justOpenedRef = useRef<boolean>(false);

  useEffect(() => {
    setVal(id || value);
    previousVal.current = value;
  }, [id, value]);

  useEffect(() => {
    latestVal.current = val;
  }, [val]);

  useEffect(() => {
    function handleClickOutside(event: Event) {
      const target = event.target as HTMLElement;
      const isInput = target.matches('.input-element') || target.closest('.input-element');

      // Don't close if we just opened the component
      if (justOpenedRef.current) {
        justOpenedRef.current = false;
        return;
      }

      if (ref.current && !ref.current.contains(target) && !isInput) {
        handleOnBlur(isDropdown ?? false, event);
      }
    }

    // Handle both mouse and touch events for better mobile support
    function handleTouchStart(event: Event) {
      const target = event.target as HTMLElement;
      if (ref.current && ref.current.contains(target)) {
        // Don't set touchStartRef if we're touching within our component
        return;
      }
      touchStartRef.current = true;
    }

    function handleTouchEnd(event: Event) {
      if (touchStartRef.current) {
        // Small delay to allow for proper touch handling
        setTimeout(() => {
          handleClickOutside(event);
          touchStartRef.current = false;
        }, 150);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDropdown]);

  useEffect(() => {
    // Focus dropdown if apiInProgress is true and it's a dropdown
    if (apiInProgress && isDropdown && dropdownRef.current) {
      dropdownRef.current.focus();
    }
  }, [apiInProgress, isDropdown]);

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | { target: { name: string; value: string } }
  ) => {
    const { name, value } = e.target;
    let newValue = value;

    const PERCENTAGE_VALIDATION = ['project_completion'];

    const MAX_10_DIGITS_ONE_DECIMAL = [
      'material_quantity',
      'labour_quantity',
      'itemQuantity',
      'labour_hours_per',
      'quantity',
      'fabrication_hours',
      'pm_hours',
      'design_hours',
      'paint_hours',
      'media_hours',
      'install_hours'
    ];

    const MAX_11_DIGITS_TWO_DECIMALS = [
      'itemPrice',
      'material_unit_cost',
      'width',
      'length',
      'labour_per_unit_hours',
      'labour_per_unit_days',
      'price',
      'material_budget',
      'labor_budget',
      'amount_funded',
      'fabrication_rate',
      'pm_rate',
      'design_rate',
      'paint_rate',
      'media_rate',
      'install_rate'
    ];

    // Always update the value first
    setVal(newValue);

    if (isDropdown) {
      setDropdownOptionListVisibility(false);
      console.log(
        'Selected Dropdown Value from EditableTableCell Component-handleChange',
        newValue
      );
      // For dropdown selections, immediately trigger the completion
      setTimeout(() => {
        const refVal = newValue === '' ? ' ' : newValue.toString();
        onEditComplete(refVal);
      }, 50);
      return;
    }

    // Handle validation for different field types
    if (MAX_10_DIGITS_ONE_DECIMAL.includes(name)) {
      if (!MAX_10_DIGITS_ONE_DECIMAL_REGEX.test(newValue)) {
        return; // Don't update if validation fails
      }
      if (newValue.startsWith('.')) {
        newValue = `0${newValue}`;
        setVal(newValue);
      }
      if (newValue.replace('.', '').length > 11) {
        return; // Don't update if too long
      }
    } else if (MAX_11_DIGITS_TWO_DECIMALS.includes(name)) {
      if (!MAX_11_DIGITS_TWO_DECIMALS_REGEX.test(newValue)) {
        return;
      }
      if (newValue.startsWith('.')) {
        newValue = `0${newValue}`;
        setVal(newValue);
      }
      if (newValue.replace('.', '').length > 11) {
        return;
      }
    } else if (PERCENTAGE_VALIDATION.includes(name)) {
      if (!PERCENTAGE_VALIDATION_REGEX.test(newValue)) {
        return;
      }
    } else if (name === 'estimate_item_type_id') {
      if (newValue === '' || newValue === 'Choose One') {
        setVal(previousVal.current);
        return;
      }
    } else if (name === 'hours') {
      if (!HOURS_VALIDATION_REGEX.test(newValue)) {
        return;
      }
    } else if (name === 'client_contact_phone') {
      if (!PHONE_VALIDATION_REGEX.test(newValue)) {
        return;
      }
    } else if (name === 'job_value') {
      if (!DECIMAL_VALIDATION_TWO_PLACES_REGEX.test(newValue)) {
        return;
      }
    }
  };

  const handleClick = () => {
    justOpenedRef.current = true;
    setApiInProgress(true);
    setDropdownOptionListVisibility(true);

    // Reset the flag after a short delay to allow normal outside click detection
    setTimeout(() => {
      justOpenedRef.current = false;
    }, 300);
  };

  const handleOnBlur = (isValId, event) => {
    if (latestVal.current === val) {
      const v = isValId ? id : value;

      // Check if the related target is within our component
      const relatedTarget = event?.relatedTarget;
      if (relatedTarget && ref.current?.contains(relatedTarget)) {
        return;
      }

      if (val === previousVal.current) {
        setApiInProgress(false);
        return;
      }

      let refVal = latestVal.current;
      if (v !== refVal) {
        refVal = refVal === '' ? ' ' : refVal.toString();
        onEditComplete(refVal);
      } else {
        setApiInProgress(false);
      }
    }
  };

  // Enhanced blur handler that works better on mobile
  const enhancedBlurHandler = (isValId) => (event) => {
    // For mobile, we need to handle this slightly differently
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

    if (isMobile) {
      // On mobile, use a small timeout to ensure the event completes
      setTimeout(() => {
        handleOnBlur(isValId, event);
      }, 100);
    } else {
      handleOnBlur(isValId, event);
    }
  };

  // Handle Enter key and Escape key
  const handleKeyDown = (isValId: boolean) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleOnBlur(isValId, event); // Submits
    } else if (event.key === 'Escape') {
      // Cancels edit and reverts
      setVal(previousVal.current);
      setApiInProgress(false);
    } else if (name === 'job_value' && (event.key === '+' || event.key === '-')) {
      event.preventDefault(); // Prevent invalid characters
    }
  };

  return apiInProgress ? (
    <div className="input-div" ref={ref}>
      {isDropdown ? (
        <DropDown
          name={name || 'editable-cell-dropdown'}
          value={val.toString()}
          onChange={(e) => {
            handleChange(e as ChangeEvent<HTMLInputElement | HTMLSelectElement>);
          }}
          onBlur={enhancedBlurHandler(true)}
          data={options}
          isDropdownOptionListVisible={isDropdownOptionListVisible}
        />
      ) : isAddress ? (
        <GooglePlacesAutocompleteComp
          value={val?.toString()}
          onChange={handleChange}
          onBlur={enhancedBlurHandler(false)}
          fromEditableTableCell
          name={'editableCell'}
        />
      ) : (
        <TextField
          className={'form-control input-element'}
          type={type}
          min={min}
          autoFocus
          max={max}
          maxLength={maxLength}
          name="editable-cell-input"
          value={val?.toString() ?? ''}
          onChange={(e) => handleChange(e)}
          onBlur={enhancedBlurHandler(false)}
          selectText={selectText}
          onKeyPress={handleKeyDown(false)}
        />
      )}
    </div>
  ) : (
    <div
      style={{
        width: '100%',
        minHeight: '20px',
        textWrap: 'wrap',
        cursor: 'pointer' // Better UX indication
      }}
      onClick={handleClick}>
      {displayValue || value}
    </div>
  );
}

export default React.memo(
  EditableTableCell,
  (prevProps, nextProps) =>
    prevProps.apiInProgress === nextProps.apiInProgress &&
    prevProps.value === nextProps.value &&
    prevProps.id === nextProps.id
);
